import Phaser from 'phaser';
import { GAME_CONFIG } from '../config';
import { AccessoryData } from '../data/accessories';
import { bezier, getFloorY, getScaleFromZ, projectX } from '../utils/coordinates';
import { HatState, Trajectory } from '../types';
import { HatShadow } from './HatShadow';

export type HatLandHandler = (hat: Hat) => void;

export class Hat {
  scene: Phaser.Scene;
  sprite: Phaser.GameObjects.Image;
  shadow: HatShadow;
  accessory: AccessoryData;

  worldX: number = GAME_CONFIG.HAT_REST_X;
  worldY: number = GAME_CONFIG.HAT_REST_Y;
  worldZ: number = -1.0;

  state: HatState = 'idle';
  trajectory: Trajectory | null = null;
  flightStartTime: number = 0;
  rotation: number = 0;
  rotationSpeed: number = 0;

  screenX: number = GAME_CONFIG.HAT_REST_X;
  screenY: number = GAME_CONFIG.HAT_REST_Y;
  currentScale: number = GAME_CONFIG.HAT_REST_SCALE;

  /** Per-state size multiplier applied in render(). Sits at HAT_REST_SCALE
   * while idle and tweens up to HAT_GRAB_SCALE the moment the player picks
   * the hat up; stays at that size through the throw so it doesn't shrink to
   * near-invisibility at the apex of the arc. reset() snaps it back. */
  private restFactor: number = GAME_CONFIG.HAT_REST_SCALE;
  private grabTween: Phaser.Tweens.Tween | null = null;

  onLand: HatLandHandler | null = null;

  constructor(scene: Phaser.Scene, accessory: AccessoryData) {
    this.scene = scene;
    this.accessory = accessory;

    this.shadow = new HatShadow(scene);

    this.sprite = scene.add.image(this.worldX, this.worldY, accessory.accIconKey);
    this.sprite.setOrigin(0.5, 0.5);
    // At rest the hat sits visually on top of HatPad (depth 70); during ascent (worldZ>0)
    // it goes behind the character. render() keeps this in sync.
    this.sprite.setDepth(75);

    this.render();
  }

  setAccessory(accessory: AccessoryData) {
    this.accessory = accessory;
    this.sprite.setTexture(accessory.accIconKey);
  }

  getMatchingHeadKey(): string {
    return this.accessory.headKey;
  }

  setState(state: HatState) {
    const prev = this.state;
    this.state = state;
    this.shadow.setVisible(state === 'flying');

    // Pop the hat up to the grab size on idle→dragging. The tween survives the
    // dragging→flying transition (we don't restart it on flying), so the size
    // is steady through the throw. render() runs continuously during flight,
    // so the tween's value propagates automatically; while still dragging we
    // call render() from the tween's onUpdate since the drag path bypasses it.
    if (state === 'dragging' && prev === 'idle') {
      if (this.grabTween) this.grabTween.stop();
      this.grabTween = this.scene.tweens.add({
        targets: this,
        restFactor: GAME_CONFIG.HAT_GRAB_SCALE,
        duration: 140,
        ease: 'Back.Out',
        onUpdate: () => this.render(),
      });
    }
  }

  setScreenPos(x: number, y: number) {
    this.screenX = x;
    this.screenY = y;
    this.worldX = x;
    this.worldY = y;
    this.sprite.setPosition(x, y);
  }

  setFlying(trajectory: Trajectory) {
    this.trajectory = trajectory;
    this.flightStartTime = performance.now();
    this.rotation = 0;
    this.rotationSpeed = trajectory.rotationSpeed;
    this.setState('flying');
  }

  reset() {
    this.state = 'idle';
    this.worldX = GAME_CONFIG.HAT_REST_X;
    this.worldY = GAME_CONFIG.HAT_REST_Y;
    this.worldZ = -1.0;
    this.rotation = 0;
    this.rotationSpeed = 0;
    this.trajectory = null;
    // Snap the grab-pop back to rest size so the next grab tweens cleanly.
    if (this.grabTween) {
      this.grabTween.stop();
      this.grabTween = null;
    }
    this.restFactor = GAME_CONFIG.HAT_REST_SCALE;
    this.sprite.setVisible(true);
    this.shadow.setVisible(false);
    this.render();
  }

  hideAfterHit() {
    const currentScale = this.sprite.scale;
    this.scene.tweens.add({
      targets: this.sprite,
      alpha: 0,
      scale: currentScale * 0.95,
      duration: 60,
      onComplete: () => {
        this.sprite.setVisible(false);
        this.sprite.setAlpha(1);
      },
    });
  }

  render() {
    const scaleZ = getScaleFromZ(this.worldZ);
    const screenX = projectX(this.worldX, this.worldZ);
    const screenY = this.worldY;

    // restFactor is HAT_REST_SCALE at idle and tweens to HAT_GRAB_SCALE on grab
    // (see setState). Multiply by the perspective scaleZ for the final on-screen size.
    const finalScale = scaleZ * this.restFactor;

    this.sprite.setPosition(screenX, screenY);
    this.sprite.setScale(finalScale);
    this.sprite.setRotation(this.rotation);
    // worldZ>0 = airborne → render behind character; otherwise on top of HatPad (depth 70).
    this.sprite.setDepth(this.worldZ > 0 ? 8 : 75);

    this.screenX = screenX;
    this.screenY = screenY;
    this.currentScale = finalScale;

    if (this.state === 'flying') {
      const shadowScreenX = projectX(this.worldX, this.worldZ);
      const shadowY = getFloorY(this.worldZ);
      const shadowScale = scaleZ * 0.8;
      const heightAboveFloor = Math.max(0, shadowY - this.worldY);
      const shadowAlpha = Math.max(0, 0.45 - heightAboveFloor * 0.0008);
      this.shadow.update(shadowScreenX, shadowY, shadowScale, shadowAlpha);
    }
  }

  updateFlight(time: number) {
    if (this.state !== 'flying' || !this.trajectory) return;

    const elapsed = time - this.flightStartTime;
    const t = Math.min(1, elapsed / this.trajectory.duration);

    this.worldX = bezier(t, this.trajectory.startX, this.trajectory.peakX, this.trajectory.endX);
    this.worldY = bezier(t, this.trajectory.startY, this.trajectory.peakY, this.trajectory.endY);
    this.worldZ = bezier(t, this.trajectory.startZ, this.trajectory.peakZ, this.trajectory.endZ);

    this.rotation += this.rotationSpeed * (1 / 60);

    this.render();

    if (t >= 1) {
      this.setState('landed');
      this.shadow.setVisible(false);
      if (this.onLand) this.onLand(this);
    }
  }
}
